import { useEffect, useState } from 'react';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { RedirectParams } from '../../../../../shared/types/redirectParams';
import { asyncGetParams } from '../../../../../services/param.service';
import { ISelect, ParamEnum } from '../../../../../shared/types/shared';
import { RedirectPqrListActions } from './actions';
import { TableTooltip } from '../../../../components/general/table';
import {
    isOnlyViewProfile,
    isOperativeProfile,
} from '../../../../../shared/types/profiles';
import { RootState } from '../../../../../redux/store';
import { useSelector } from 'react-redux';
import { DateTooltip } from '../../../../components/general/dateTooltip';

export const useRedirectPqrList = (filters?: RedirectParams) => {
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<RedirectParams[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<RedirectParams | object>(
        {}
    );

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<RedirectParams>[] = [];

    if (!isOnlyViewProfile(type) && !isOperativeProfile(type)) {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <div className="no-click-cell">
                    <RedirectPqrListActions id={row.id} />
                </div>
            ),
        });
    }

    columns.push(
        {
            field: 'name',
            headerName: 'Nombre',
            minWidth: 120,
            flex: 0.5,
        },
        {
            field: 'extra',
            headerName: 'Correos',
            minWidth: 120,
            flex: 0.5,
            renderCell: (row) => (
                <TableTooltip value={row.row.extra} isMultiline={true} />
            ),
        },
        {
            field: 'parent',
            headerName: 'Área',
            minWidth: 120,
            flex: 0.5,
            renderCell: (row) => (
                <TableTooltip value={(row.row.parent as ISelect)?.name || ''} />
            ),
        },
        {
            field: 'active',
            headerName: 'Activo',
            minWidth: 80,
            flex: 0.4,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <>{row.row.active ? 'Si' : 'No'}</>,
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <DateTooltip date={row.row.created_at} />,
        }
    );

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        let name: string | undefined;
        let extra: string | undefined;
        let parent: string | ISelect | undefined;
        let active: boolean | undefined;

        if (
            finalFilters &&
            'name' in finalFilters &&
            'extra' in finalFilters &&
            'active' in finalFilters
        ) {
            ({ name, extra, parent, active } = finalFilters);
        }

        asyncGetParams(
            ParamEnum.PQR_REDIRECTION_PERSON,
            '',
            parent,
            name,
            extra,
            active,
            page,
            pageSize
        )
            .then((res: any) => {
                const items = res?.items || [];
                const processedItems = items.map((item: RedirectParams) => ({
                    ...item,
                    extra: item.extra
                        ? item.extra.replace(/;/g, '\n').toLowerCase()
                        : '',
                }));
                setRows(processedItems);
                setTotal(res?.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters]);

    return {
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
